import React, { useState, useEffect, useRef } from 'react';
import { User } from '../../types';
import ThemeToggle from '../common/ThemeToggle';

interface SidebarProps {
  user: User | null;
  currentView: string;
  onViewChange: (view: string) => void;
  onCreateTicket: () => void;
  onLogout: () => void;
  onProfileUpdate?: (user: User) => void;
  isMobile?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  soundEnabled?: boolean;
  onToggleSound?: () => void;
  onTestSound?: (soundType: 'green' | 'yellow' | 'red' | 'update') => void;
}

type SidebarMode = 'fixed-open' | 'fixed-closed' | 'auto-hide';

const Sidebar: React.FC<SidebarProps> = ({
  user,
  currentView,
  onViewChange,
  onCreateTicket,
  onLogout,
  onProfileUpdate,
  isMobile = false,
  onClose,
  collapsed = false,
  onToggleCollapse,
  soundEnabled = true,
  onToggleSound,
  onTestSound
}) => {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('fixed-open');
  const [showSettings, setShowSettings] = useState(false);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const autoCollapseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const soundSettingsRef = useRef<HTMLDivElement>(null);

  // Load sidebar mode preference
  useEffect(() => {
    const savedMode = localStorage.getItem('sidebar-mode') as SidebarMode;
    if (savedMode) {
      setSidebarMode(savedMode);
    }
  }, []);

  // Handle auto-hide mode
  useEffect(() => {
    if (isMobile || sidebarMode !== 'auto-hide') return;

    // Clear existing timer
    if (autoCollapseTimerRef.current) {
      clearTimeout(autoCollapseTimerRef.current);
    }

    // If not hovered and not collapsed, set timer to collapse
    if (!isHovered && !collapsed) {
      autoCollapseTimerRef.current = setTimeout(() => {
        if (onToggleCollapse && !isHovered) {
          onToggleCollapse();
        }
      }, 3000); // 3 seconds of inactivity
    }

    // If hovered and collapsed, expand
    if (isHovered && collapsed) {
      if (onToggleCollapse) {
        onToggleCollapse();
      }
    }

    return () => {
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current);
      }
    };
  }, [sidebarMode, isHovered, collapsed, onToggleCollapse, isMobile]);

  // Apply sidebar mode
  useEffect(() => {
    if (isMobile || !onToggleCollapse) return;

    if (sidebarMode === 'fixed-open' && collapsed) {
      onToggleCollapse();
    } else if (sidebarMode === 'fixed-closed' && !collapsed) {
      onToggleCollapse();
    }
  }, [sidebarMode, isMobile]);

  // Close settings when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
      if (soundSettingsRef.current && !soundSettingsRef.current.contains(event.target as Node)) {
        setShowSoundSettings(false);
      }
    };

    if (showSettings || showSoundSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings, showSoundSettings]);

  const handleModeChange = (mode: SidebarMode) => {
    setSidebarMode(mode);
    localStorage.setItem('sidebar-mode', mode);
    setShowSettings(false);
  };
  // Base navigation for customers
  const navigation = [];

  if (user?.userType === 'agent') {
    // Agent navigation in the requested order - Board View first
    navigation.push(
      {
        name: 'Board View',
        id: 'board',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
          </svg>
        ),
      },
      {
        name: 'All Open Tickets',
        id: 'all-open-tickets',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        ),
      },
      {
        name: 'My Open Tickets',
        id: 'my-open-tickets',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
      },
      {
        name: 'Unassigned',
        id: 'unassigned',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      {
        name: 'All Tickets',
        id: 'tickets',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        ),
      },
      {
        name: 'My Tickets',
        id: 'my-tickets',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      },
      {
        name: 'All Closed',
        id: 'resolved',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
      {
        name: 'Service Tickets',
        id: 'service-tickets',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        ),
      }
    );

    // Add Insights section for agents with proper permissions
    if (user?.userType === 'agent' && user?.permissions?.includes('insights.view')) {
      navigation.push({
        name: 'Insights',
        id: 'insights',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      });
    }

    // Add Customer Management section for agents with proper permissions
    if (user?.userType === 'agent' && user?.permissions?.includes('customers.view')) {
      navigation.push({
        name: 'Customers',
        id: 'customers',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
      });
    }

    // Add Device Management section for agents with proper permissions
    console.log('🔧 Device permission check:', {
      userRole: user?.role,
      userPermissions: user?.permissions,
      hasDevicesView: user?.permissions?.includes('devices.view')
    });
    
    if (user?.userType === 'agent' && user?.permissions?.includes('devices.view')) {
      console.log('✅ Adding Devices navigation item');
      navigation.push({
        name: 'Devices',
        id: 'devices',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 002 2v10a2 2 0 002 2z" />
          </svg>
        ),
      });
    } else {
      console.log('❌ Not adding Devices navigation item');
    }

    // Add Company Management section for agents with proper permissions
    if (user?.userType === 'agent' && user?.permissions?.includes('companies.view')) {
      navigation.push({
        name: 'Companies',
        id: 'companies',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        ),
      });
    }

    // Add File Management section for agents with proper permissions
    if (user?.userType === 'agent' && user?.permissions?.includes('tickets.view_all')) {
      navigation.push({
        name: 'Files',
        id: 'files',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      });
    }



  } else {
    // Customer navigation (simplified)
    navigation.push(
      {
        name: 'All Tickets',
        id: 'tickets',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        ),
      },
      {
        name: 'My Tickets',
        id: 'my-tickets',
        icon: (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      }
    );
  }

  const handleItemClick = (itemId: string) => {
    onViewChange(itemId);
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <div 
      className={`flex flex-col h-full overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${collapsed && !isMobile ? 'w-16' : 'w-full'}`}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
    >
      {/* Close button for mobile */}
      {isMobile && (
        <>
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Menu</h2>
            <button
              onClick={onClose}
              className="rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Sound and Theme Controls for Mobile */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-center gap-2">
            {/* Sound Settings Button */}
            <div className="relative" ref={soundSettingsRef}>
              <button
                onClick={() => setShowSoundSettings(!showSoundSettings)}
                className={`p-2 rounded-md transition-colors ${
                  soundEnabled
                    ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-800'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title={soundEnabled ? "Sound enabled" : "Sound disabled"}
              >
                {soundEnabled ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                )}
              </button>

              {/* Sound Settings Dropdown */}
              {showSoundSettings && (
                <div className="fixed left-4 right-4 top-20 w-auto max-w-sm bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 z-[60] max-h-[70vh] overflow-y-auto">
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Sound Notifications</h3>
                    
                    {/* Master Toggle */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Enable Sounds</span>
                      <button
                        onClick={onToggleSound}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          soundEnabled ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                            soundEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Sound Tests */}
                    {soundEnabled && onTestSound && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Test Sounds</h4>
                        
                        <button
                          onClick={() => onTestSound('green')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                        >
                          <span>New/Resolved</span>
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </button>
                        
                        <button
                          onClick={() => onTestSound('yellow')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 rounded-md hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                        >
                          <span>Attention Needed</span>
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        </button>
                        
                        <button
                          onClick={() => onTestSound('red')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        >
                          <span>Critical</span>
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        </button>
                        
                        <button
                          onClick={() => onTestSound('update')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                          <span>Update</span>
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <ThemeToggle />
          </div>
        </>
      )}

      {/* Collapse toggle and settings for desktop */}
      {!isMobile && (
        <div className="p-3 border-b-2 border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
          {!collapsed ? (
            /* Expanded view - show all controls in a row */
            <div className="flex items-center justify-between gap-2">
              {onToggleCollapse && (
                <button
                  onClick={onToggleCollapse}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 border border-gray-300 dark:border-gray-600 shadow-sm hover:shadow transition-all"
                  title="Collapse sidebar"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                  <span className="text-sm font-medium">Collapse</span>
                </button>
              )}
              
              {/* Settings button */}
              <div className="relative" ref={settingsRef}>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 border border-gray-300 dark:border-gray-600 shadow-sm hover:shadow transition-all"
                title="Sidebar mode settings"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>

              {/* Settings Dropdown */}
              {showSettings && (
                <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50 border border-gray-200 dark:border-gray-700">
                  <div className="py-1">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Sidebar Mode
                    </div>
                    <button
                      onClick={() => handleModeChange('fixed-open')}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        sidebarMode === 'fixed-open'
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <div>
                          <div className="font-medium">Fixed Open</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Always expanded</div>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeChange('fixed-closed')}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        sidebarMode === 'fixed-closed'
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                        </svg>
                        <div>
                          <div className="font-medium">Fixed Closed</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Always collapsed</div>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeChange('auto-hide')}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        sidebarMode === 'auto-hide'
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <div className="font-medium">Auto-Hide</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Hides after 3 seconds</div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          ) : (
            /* Collapsed view - show only expand button */
            <div className="flex items-center justify-center">
              {onToggleCollapse && (
                <button
                  onClick={onToggleCollapse}
                  className="p-3 rounded-lg bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 border-2 border-primary-500 dark:border-primary-600 shadow-md hover:shadow-lg transition-all"
                  title="Expand sidebar"
                >
                  <svg className="h-6 w-6 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Sound and Theme Controls - Always visible */}
          <div className={`flex items-center gap-2 mt-2 pt-2 border-t border-gray-300 dark:border-gray-600 ${collapsed ? 'justify-center flex-col' : 'justify-center'}`}>
            {/* Sound Settings Button */}
            <div className="relative" ref={soundSettingsRef}>
              <button
                onClick={() => setShowSoundSettings(!showSoundSettings)}
                className={`p-2 rounded-md transition-colors ${
                  soundEnabled
                    ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-800'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title={soundEnabled ? "Sound enabled" : "Sound disabled"}
              >
                {soundEnabled ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                )}
              </button>

              {/* Sound Settings Dropdown */}
              {showSoundSettings && (
                <div className={`fixed w-72 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 z-[60] max-h-[80vh] overflow-y-auto ${
                  collapsed 
                    ? 'left-20 bottom-4' // When collapsed, position to the right of sidebar
                    : 'left-20 bottom-4' // When expanded, position next to sidebar
                }`}>
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Sound Notifications</h3>
                    
                    {/* Master Toggle */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-700 dark:text-gray-300">Enable Sounds</span>
                      <button
                        onClick={onToggleSound}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          soundEnabled ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                            soundEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Sound Tests */}
                    {soundEnabled && onTestSound && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Test Sounds</h4>
                        
                        <button
                          onClick={() => onTestSound('green')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                        >
                          <span>New/Resolved</span>
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </button>
                        
                        <button
                          onClick={() => onTestSound('yellow')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 rounded-md hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                        >
                          <span>Attention Needed</span>
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        </button>
                        
                        <button
                          onClick={() => onTestSound('red')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        >
                          <span>Critical</span>
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        </button>
                        
                        <button
                          onClick={() => onTestSound('update')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                          <span>Update</span>
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <ThemeToggle />
          </div>
        </div>
      )}

      {/* Logo and title */}
      {!collapsed && !isMobile && (
        <div className="flex items-center px-4 py-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <img 
              src="/neurovirtual-logo.png" 
              alt="NeuroVirtual Logo" 
              className="h-10 w-10 object-contain"
            />
            <div className="ml-3">
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">NeuroVirtual</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Support Platform</p>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed logo */}
      {collapsed && !isMobile && (
        <div className="flex items-center justify-center py-6 border-b border-gray-200 dark:border-gray-700">
          <img 
            src="/neurovirtual-logo.png" 
            alt="NeuroVirtual Logo" 
            className="h-8 w-8 object-contain"
            title="NeuroVirtual"
          />
        </div>
      )}

      {/* User profile */}
      {user && !collapsed && (
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            {user.avatarUrl ? (
              <img 
                src={user.avatarUrl.startsWith('/uploads/avatars/') 
                  ? `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}${user.avatarUrl}`
                  : user.avatarUrl
                } 
                alt="Profile" 
                className="h-10 w-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`h-10 w-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center ${user.avatarUrl ? 'hidden' : ''}`}>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {user.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.userType}</p>
              {user.userType === 'agent' && user.roleName && (
                <p className="text-xs text-gray-400 dark:text-gray-500">{user.roleName}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Collapsed user avatar */}
      {user && collapsed && !isMobile && (
        <div className="flex items-center justify-center py-4 border-b border-gray-200 dark:border-gray-700">
          {user.avatarUrl ? (
            <img 
              src={user.avatarUrl.startsWith('/uploads/avatars/') 
                ? `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}${user.avatarUrl}`
                : user.avatarUrl
              } 
              alt="Profile" 
              className="h-8 w-8 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
              onError={(e) => {
                // Fallback to initials if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div className={`h-8 w-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center ${user.avatarUrl ? 'hidden' : ''}`}>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {user.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1">
        {/* Create ticket button */}
        {user?.userType === 'customer' && (
          <button
            onClick={() => {
              onCreateTicket();
              if (isMobile && onClose) onClose();
            }}
            className={`w-full group flex items-center ${collapsed && !isMobile ? 'justify-center' : 'px-2'} py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors`}
            title={collapsed ? "New Ticket" : ""}
          >
            <svg className={`h-5 w-5 ${collapsed && !isMobile ? '' : 'mr-3'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {!collapsed && "New Ticket"}
          </button>
        )}

        {/* Navigation items */}
        {navigation.map((item) => (
          <button
            key={item.id}
            onClick={() => handleItemClick(item.id)}
            className={`w-full group flex items-center ${collapsed && !isMobile ? 'justify-center' : 'px-2'} py-2 text-sm font-medium rounded-md transition-colors ${
              currentView === item.id
                ? 'bg-primary-100 dark:bg-primary-900 text-primary-900 dark:text-primary-100 border-r-2 border-primary-600'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
            title={collapsed ? item.name : ""}
          >
            <span className={`h-5 w-5 ${collapsed && !isMobile ? '' : 'mr-3'} ${currentView === item.id ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`}>
              {item.icon}
            </span>
            {!collapsed && item.name}
          </button>
        ))}
      </nav>

      {/* Audit Trail, User Management, My Account and Logout buttons */}
      <div className="px-2 pb-4 space-y-1">
        {/* System Management button - only for agents with access */}
        {user?.userType === 'agent' && (
          user.permissions?.includes('system.management') ||
          user.permissions?.includes('system.user_management') ||
          user.permissions?.includes('system.role_management') ||
          user.roleName === 'Admin'
        ) && (
          <button
            onClick={() => handleItemClick('users')}
            className={`w-full group flex items-center ${collapsed && !isMobile ? 'justify-center' : 'px-2'} py-2 text-sm font-medium rounded-md transition-colors ${
              currentView === 'users'
                ? 'bg-primary-100 dark:bg-primary-900 text-primary-900 dark:text-primary-100 border-r-2 border-primary-600'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
            title={collapsed ? "System Management" : ""}
          >
            <svg className={`h-5 w-5 ${collapsed && !isMobile ? '' : 'mr-3'} ${currentView === 'users' ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            {!collapsed && "System Management"}
          </button>
        )}

        {/* My Account button - only for agents */}
        {user?.userType === 'agent' && (
          <button
            onClick={() => onViewChange('account')}
            className={`w-full group flex items-center ${collapsed && !isMobile ? 'justify-center' : 'px-2'} py-2 text-sm font-medium rounded-md ${
              currentView === 'account'
                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
            } transition-colors`}
            title={collapsed ? "My Account" : ""}
          >
            <svg className={`h-5 w-5 ${collapsed && !isMobile ? '' : 'mr-3'} ${
              currentView === 'account'
                ? 'text-blue-500 dark:text-blue-400'
                : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'
            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {!collapsed && "My Account"}
          </button>
        )}
        
        {/* Logout button */}
        <button
          onClick={onLogout}
          className={`w-full group flex items-center ${collapsed && !isMobile ? 'justify-center' : 'px-2'} py-2 text-sm font-medium rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors`}
          title={collapsed ? "Sign out" : ""}
        >
          <svg className={`h-5 w-5 ${collapsed && !isMobile ? '' : 'mr-3'} text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          {!collapsed && "Sign out"}
        </button>
      </div>


    </div>
  );
};

export default Sidebar; 